import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ManifestPodPreview,
  ManifestPodShipment,
} from 'src/app/features/model/hawb.model';
import { SvgIconComponent } from 'src/app/shared/components/svg-icon/svg-icon.component';
import { MaterialModule } from 'src/app/shared/material.module';
import {
  ModalPopupMessages,
  popupButtons,
} from 'src/app/shared/utility/constants';
import {
  getManifestPodBannerMessage,
  isManifestPodAllHeld,
} from 'src/app/shared/utility/pod-details.util';

export type ManifestPodLinkedShipmentsMode = 'select' | 'view';

export interface ManifestPodLinkedShipmentsDialogData {
  /** `select`: input-row preview (FR-BPM-UI-007…011); `view`: saved-row snapshot (FR-BPM-UI-017). */
  mode: ManifestPodLinkedShipmentsMode;
  shipments: ManifestPodShipment[];
  selectedHawbIds?: number[];
  preview?: ManifestPodPreview | null;
}

/** Closes with the confirmed `hawbId`s, or `false` on Close. */
@Component({
  selector: 'app-manifest-pod-linked-shipments-popup',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    SvgIconComponent,
  ],
  templateUrl: './manifest-pod-linked-shipments-popup.component.html',
  styleUrl: './manifest-pod-linked-shipments-popup.component.scss',
})
export class ManifestPodLinkedShipmentsPopupComponent implements OnInit {
  private readonly dialogRef = inject(
    MatDialogRef<ManifestPodLinkedShipmentsPopupComponent>
  );
  private readonly dialogData = inject(MAT_DIALOG_DATA);
  private readonly destroyRef = inject(DestroyRef);

  public readonly popupButtons = popupButtons;
  public readonly noMatchMessage =
    ModalPopupMessages.manifestPodNoMatchingShipments;
  public readonly searchControl = new FormControl<string>('', {
    nonNullable: true,
  });
  public dialogTitle = ModalPopupMessages.manifestPodLinkedShipmentsHeader;
  public isSelectMode = false;
  public isGridDisabled = false;
  public bannerMessage: string | null = null;
  public shipments: ManifestPodShipment[] = [];
  public visibleShipments: ManifestPodShipment[] = [];
  public selectedIds = new Set<number>();

  ngOnInit(): void {
    const payload: ManifestPodLinkedShipmentsDialogData =
      this.dialogData?.code ?? this.dialogData;
    this.dialogTitle = this.dialogData?.title ?? this.dialogTitle;
    this.isSelectMode = payload.mode === 'select';
    this.shipments = payload.shipments ?? [];
    this.visibleShipments = this.shipments;

    if (this.isSelectMode) {
      this.bannerMessage = getManifestPodBannerMessage(payload.preview);
      this.isGridDisabled =
        !this.shipments.length || isManifestPodAllHeld(payload.preview);
      this.selectedIds = new Set(
        payload.selectedHawbIds ?? this.shipments.map(row => row.hawbId)
      );
      if (this.isGridDisabled) {
        this.searchControl.disable({ emitEvent: false });
      }
    }

    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => this.applySearch(value));
  }

  public get canConfirm(): boolean {
    return !this.isGridDisabled && this.selectedIds.size > 0;
  }

  public get isAllVisibleSelected(): boolean {
    return (
      this.visibleShipments.length > 0 &&
      this.visibleShipments.every(row => this.selectedIds.has(row.hawbId))
    );
  }

  public get isSomeVisibleSelected(): boolean {
    return (
      !this.isAllVisibleSelected &&
      this.visibleShipments.some(row => this.selectedIds.has(row.hawbId))
    );
  }

  public toggleAllVisible(checked: boolean): void {
    this.visibleShipments.forEach(row =>
      checked
        ? this.selectedIds.add(row.hawbId)
        : this.selectedIds.delete(row.hawbId)
    );
  }

  public toggleRow(hawbId: number, checked: boolean): void {
    if (checked) {
      this.selectedIds.add(hawbId);
    } else {
      this.selectedIds.delete(hawbId);
    }
  }

  public trackByHawbId(_index: number, row: ManifestPodShipment): number {
    return row.hawbId;
  }

  public onClose(): void {
    this.dialogRef.close(false);
  }

  public onConfirm(): void {
    if (!this.canConfirm) {
      return;
    }
    // Keep the preview order so the payload is stable.
    this.dialogRef.close(
      this.shipments
        .map(row => row.hawbId)
        .filter(id => this.selectedIds.has(id))
    );
  }

  /** FR-BPM-UI-009: client-side, Shipment number only, case-insensitive contains. */
  private applySearch(value: string): void {
    const term = value.trim().toLowerCase();
    this.visibleShipments = term
      ? this.shipments.filter(row =>
          (row.hawbExt ?? '').toLowerCase().includes(term)
        )
      : this.shipments;
  }
}
